/**
 * SoleadLab 2.0 — IV Report Preview
 */

import { useState } from 'react';
import { useIVStore } from '@/stores/ivStore';
import * as ivApi from '@/services/ivApi';

interface Props {
  sessionId: string;
}

export default function IVReportPreview({ sessionId }: Props) {
  const findings = useIVStore((s) => s.findings);
  const reports = useIVStore((s) => s.reports);
  const generateReport = useIVStore((s) => s.generateReport);

  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const handleGenerate = async () => {
    setGenerating(true);
    setError('');
    try {
      await generateReport(sessionId);
    } catch (err) {
      setError(String(err));
    } finally {
      setGenerating(false);
    }
  };

  const handlePreview = async (reportId: string) => {
    setLoadingPreview(true);
    try {
      const report = await ivApi.getReport(reportId);
      setPreviewHtml(report.html_content ?? null);
    } catch (err) {
      setError(String(err));
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleDownload = () => {
    if (!previewHtml) return;
    const blob = new Blob([previewHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'iv-report.html';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
        <button
          className="iv-btn iv-btn--primary"
          onClick={handleGenerate}
          disabled={generating || findings.length === 0}
        >
          {generating ? 'Generating...' : 'Generate Report'}
        </button>
        {findings.length === 0 && (
          <span style={{ fontSize: 'var(--sl-text-xs)', color: 'var(--text-muted)' }}>
            Run analysis first to generate findings
          </span>
        )}
      </div>

      {error && <div className="iv-note iv-note--error">{error}</div>}

      <div className="iv-note iv-note--info">
        PDF export is available via browser print (Ctrl+P) when previewing the report.
      </div>

      {/* Report list */}
      {reports.length > 0 && (
        <div className="iv-report-list">
          <div style={{ fontSize: 'var(--sl-text-sm)', fontWeight: 600, marginBottom: 8 }}>
            Available Reports ({reports.length})
          </div>
          {reports.map((r) => (
            <div key={r.id} className="iv-report-item">
              <div>
                <div style={{ fontWeight: 500 }}>{r.title}</div>
                <div style={{ fontSize: 'var(--sl-text-xs)', color: 'var(--text-muted)' }}>
                  {new Date(r.generated_at).toLocaleString()} by {r.generated_by}
                </div>
              </div>
              <button
                className="iv-btn iv-btn--sm"
                onClick={() => handlePreview(r.id)}
                disabled={loadingPreview}
              >
                {loadingPreview ? '...' : 'Preview'}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Preview */}
      {previewHtml && (
        <div>
          <div style={{ marginBottom: 8, display: 'flex', gap: 8 }}>
            <button className="iv-btn iv-btn--sm" onClick={handleDownload}>Download HTML</button>
            <button className="iv-btn iv-btn--sm" onClick={() => setPreviewHtml(null)}>Close Preview</button>
          </div>
          <div className="iv-report-preview">
            <iframe
              srcDoc={previewHtml}
              title="IV Report Preview"
              style={{ width: '100%', minHeight: 600, border: 'none' }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
